import PreviewLayout from "./preview-layout";
import PreviewField from "./preview-field";
import type { FlowSection, FlowSectionField } from "../form-builder/types";
import type { BrandKit } from "@/modules/brand";

interface PreviewCompanyDetailProps {
	id: string;
	section: FlowSection;
	brandName: string;
	brandLogo: string | null;
	brandkit: BrandKit;
	isSelected: boolean;
	onTitleChange: (title: string) => void;
	onDescriptionChange: (description: string) => void;
	onFieldUpdate?: (fieldId: string, updates: Partial<FlowSectionField>) => void;
	isDarkMode?: boolean;
}

export default function PreviewCompanyDetail({ id, section, brandName, brandLogo, brandkit, isSelected, onTitleChange, onDescriptionChange, onFieldUpdate, isDarkMode = false }: PreviewCompanyDetailProps) {
	void isSelected;

	const visibleFields = (section.fields ?? []).filter((f) => f.is_visible).sort((a, b) => a.sort_order - b.sort_order);

	return (
		<PreviewLayout
			id={id}
			brandName={brandName}
			brandLogo={brandLogo}
			brandkit={brandkit}
			title={section.title}
			titlePlaceholder="About your company"
			subtitle={section.description}
			subtitlePlaceholder="Add a description..."
			showBackButton
			progress={{ current: 5, total: 7 }}
			centered={false}
			editableTitle
			editableSubtitle
			onLocalTitleChange={onTitleChange}
			onLocalSubtitleChange={onDescriptionChange}
			isDarkMode={isDarkMode}
		>
			<div className="space-y-8">
				{visibleFields.length > 0 ? (
					visibleFields.map((field) => (
						<PreviewField
							key={field.id}
							field={field}
							onLabelChange={(label) => onFieldUpdate?.(field.id, { label })}
							onPlaceholderChange={(placeholder) => onFieldUpdate?.(field.id, { placeholder })}
							isDarkMode={isDarkMode}
						/>
					))
				) : (
					<>
						{/* Fallback: hardcoded fields for backwards compatibility */}
						<div>
							<label className={`block font-medium text-sm mb-2 ${isDarkMode ? "text-white" : "text-primary"}`} style={{ fontFamily: "var(--font-body, inherit)" }}>
								Job title
							</label>
							<div
								className={`w-full border rounded-lg shadow-sm py-2 px-4 text-sm cursor-not-allowed ${isDarkMode ? "bg-gray-900 border-gray-800" : "bg-white border-gray-200"}`}
								style={{ fontFamily: "var(--font-body, inherit)" }}
							>
								<span className={`font-light ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}>Head of Marketing</span>
							</div>
						</div>
						<div>
							<label className={`block font-medium text-sm mb-2 ${isDarkMode ? "text-white" : "text-primary"}`} style={{ fontFamily: "var(--font-body, inherit)" }}>
								Company
							</label>
							<div
								className={`w-full border rounded-lg shadow-sm py-2 px-4 text-sm cursor-not-allowed ${isDarkMode ? "bg-gray-900 border-gray-800" : "bg-white border-gray-200"}`}
								style={{ fontFamily: "var(--font-body, inherit)" }}
							>
								<span className={`font-light ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}>Acme Inc.</span>
							</div>
						</div>
						<div>
							<label className={`block font-medium text-sm mb-2 ${isDarkMode ? "text-white" : "text-primary"}`} style={{ fontFamily: "var(--font-body, inherit)" }}>
								Website
							</label>
							<div
								className={`w-full border rounded-lg shadow-sm py-2 px-4 text-sm cursor-not-allowed ${isDarkMode ? "bg-gray-900 border-gray-800" : "bg-white border-gray-200"}`}
								style={{ fontFamily: "var(--font-body, inherit)" }}
							>
								<span className={`font-light ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}>https://acme.com</span>
							</div>
						</div>
						<div>
							<label className={`block font-medium text-sm mb-2 ${isDarkMode ? "text-white" : "text-primary"}`} style={{ fontFamily: "var(--font-body, inherit)" }}>
								Company logo
							</label>
							<div
								className={`h-20 border border-dashed rounded-lg flex items-center justify-center cursor-not-allowed ${isDarkMode ? "border-gray-700 bg-gray-900" : "border-gray-300 bg-gray-50"}`}
							>
								<svg className={`w-8 h-8 ${isDarkMode ? "text-gray-600" : "text-gray-300"}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
									<path
										strokeLinecap="round"
										strokeLinejoin="round"
										d="M2.25 21h19.5m-18-18v18m10.5-18v18m6-13.5V21M6.75 6.75h.75m-.75 3h.75m-.75 3h.75m3-6h.75m-.75 3h.75m-.75 3h.75M6.75 21v-3.375c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21M3 3h12m-.75 4.5H21m-3.75 3.75h.008v.008h-.008v-.008zm0 3h.008v.008h-.008v-.008zm0 3h.008v.008h-.008v-.008z"
									/>
								</svg>
							</div>
						</div>
					</>
				)}
			</div>
		</PreviewLayout>
	);
}
